import React from 'react';
import { IonItem, IonLabel } from '@ionic/react';
import SectionCard from '@/components/SectionCard';
import EngagementMetrics from '../EngagementMetrics';
import NewsLeaderboard, { useNewsRuns } from '../NewsLeaderboard';
import type { Topic } from '../types';
import ImpressionGoalProgress, {
  useEffectiveImpressionGoal,
} from './ImpressionGoalProgress';

interface CompositeEngagementBlockProps {
  topicId: string;
  topicTitle?: string;
  /** Navigate to the full engagement-metrics page. */
  onMetricsOpen: (path: string) => void;
  /** When set, the impression-goal progress block renders below the chart. */
  topic?: Topic;
  /** Renders the news leaderboard below the goal block (news topics). */
  showLeaderboard?: boolean;
}

/**
 * The one "Engagement" card of a topic's read view: the compact impressions
 * chart (always), the impression-goal progress (when a topic is supplied and
 * has a goal), and the news race leaderboard (news topics). The goal and the
 * leaderboard describe the same target as the chart, so they belong beside it
 * rather than in cards of their own.
 *
 * Each block is an ordinary list item, so the card's standard `lines`
 * separators divide them; the chart's item is also the tap target for the full
 * metrics page.
 */
const CompositeEngagementBlock: React.FC<CompositeEngagementBlockProps> = ({
  topicId,
  topicTitle,
  onMetricsOpen,
  topic,
  showLeaderboard = false,
}) => {
  // Both optional blocks hide themselves when they have nothing to show, so
  // ask up front: an item rendered for an absent block is an empty row with a
  // separator. The leaderboard only knows once its fetch lands, and skips that
  // fetch entirely on a topic that never asks for it.
  const hasGoal = useEffectiveImpressionGoal(topic) != null;
  const runs = useNewsRuns(showLeaderboard ? topicId : undefined);

  return (
    <SectionCard title="Engagement">
      <IonItem
        lines={hasGoal || runs ? 'inset' : 'none'}
        button
        detail
        data-testid="topic-engagement-link"
        onClick={() =>
          onMetricsOpen(
            `/topics/${encodeURIComponent(topicId)}/engagement-metrics?n=${encodeURIComponent(topicTitle ?? '')}`
          )
        }
      >
        <IonLabel>
          <EngagementMetrics topicId={topicId} compact />
        </IonLabel>
      </IonItem>
      {topic && hasGoal && (
        <IonItem lines={runs ? 'inset' : 'none'}>
          <IonLabel>
            <ImpressionGoalProgress topic={topic} />
          </IonLabel>
        </IonItem>
      )}
      {runs && (
        <IonItem lines="none">
          <IonLabel>
            <NewsLeaderboard runs={runs} />
          </IonLabel>
        </IonItem>
      )}
    </SectionCard>
  );
};

export default CompositeEngagementBlock;
